import { Component, Prop, h, Host, Element, State } from '@stencil/core';
import { uuidv4 } from '@/globals/helpers';
import { check } from 'pn-design-assets/pn-assets/icons.js';

/**
 * The `pn-choice-chip` is built with a native `input[type="checkbox|radio"]` in the background.
 * This means you can use native events to listen to the changes.
 *
 * @nativeChange Use the `change` event to listen when the choice chip state is being toggled. Works for both checkbox/radio variant.
 */
@Component({
  tag: 'pn-choice-chip',
  styleUrl: 'pn-choice-chip.scss',
})
export class PnChoiceChip {
  @Element() hostElement: HTMLElement;

  @State() moving: boolean = false;

  /** The choice chip label */
  @Prop() label!: string;
  /** This will be emitted on change and input */
  @Prop({ reflect: true }) value!: string;
  /** The name of the checkbox group */
  @Prop() name?: string;
  /** Precheck the choice chip */
  @Prop({ mutable: true }) checked?: boolean = false;
  /** Make the choice chip a radio */
  @Prop() radio: boolean = false;
  /** A unique HTML id */
  @Prop() choiceid: string = `pn-choice-chip-${uuidv4()}`;

  /** Include an icon */
  @Prop() icon?: string;

  /** Set the choice chip as required. @category State */
  @Prop() required: boolean = false;
  /** Disable the choice chip. @category State */
  @Prop() disabled: boolean = false;

  /** Set the size to small. @category Size */
  @Prop() small: boolean = false;
  /** Set the size to large. @category Size */
  @Prop() large: boolean = false;

  render() {
    return (
      <Host>
        <div class="pn-choice-chip">
          <input
            type={this.radio ? 'radio' : 'checkbox'}
            id={this.choiceid}
            value={this.value}
            name={this.name}
            checked={this.checked}
            required={this.required}
            disabled={this.disabled}
          />
          <div class="pn-choice-chip-container" data-icon={!!this.icon} data-small={this.small} data-large={this.large}>
            <div class="pn-choice-chip-icon">
              <pn-icon color="blue700" class="pn-choice-chip-icon-check" icon={check} />
              {!!this.icon && <pn-icon color="blue700" class="pn-choice-chip-icon-user" icon={this.icon} />}
            </div>
            <label class="pn-choice-chip-label" htmlFor={this.choiceid}>
              <span>{this.label}</span>
              <slot />
            </label>
          </div>
        </div>
      </Host>
    );
  }
}
